<script setup lang="ts">
  import { computed, reactive } from "vue";
  import { Square, SquareCheck } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import type { AssistantAnswer, AssistantQuestionForm } from "@stores/assistant";
  import { answerIncludes, answersMessage, hasAnswer } from "@utils/assistantQuestionForm";

  const props = withDefaults(defineProps<{ form: AssistantQuestionForm; disabled?: boolean }>(), { disabled: false });

  const emit = defineEmits<{ submit: [text: string, answers: Record<string, AssistantAnswer>] }>();

  const picked = reactive<Record<string, string[]>>({});
  const otherOpen = reactive<Record<string, boolean>>({});
  const otherText = reactive<Record<string, string>>({});

  const submitted = computed(() => Boolean(props.form.answers));

  function isMultiple(index: number): boolean {
    return Boolean(props.form.questions[index]?.multiple);
  }

  function answerOf(index: number): AssistantAnswer {
    if (submitted.value) return props.form.answers?.[String(index)] ?? "";

    const other = otherOpen[index] ? otherText[index]?.trim() ?? "" : "";
    const choices = picked[index] ?? [];
    if (isMultiple(index)) return other ? [...choices, other] : choices;
    return otherOpen[index] ? other : choices[0] ?? "";
  }

  const answeredCount = computed(() => props.form.questions.filter((_, index) => hasAnswer(answerOf(index))).length);
  const complete = computed(() => answeredCount.value === props.form.questions.length);

  function pick(index: number, option: string) {
    if (!isMultiple(index)) {
      picked[index] = [option];
      otherOpen[index] = false;
      return;
    }

    const choices = picked[index] ?? [];
    picked[index] = choices.includes(option) ? choices.filter((choice) => choice !== option) : [...choices, option];
  }

  function openOther(index: number) {
    if (isMultiple(index)) {
      otherOpen[index] = !otherOpen[index];
      return;
    }

    otherOpen[index] = true;
    delete picked[index];
  }

  function submit() {
    const answers = Object.fromEntries(props.form.questions.map((_, index) => [String(index), answerOf(index)]));
    emit("submit", answersMessage(props.form.questions, answers), answers);
  }
</script>

<template>
  <div class="flex flex-col gap-3 rounded-xl border border-border bg-card p-3">
    <div
      v-for="(item, index) in form.questions"
      :key="index"
      class="flex flex-col gap-1.5">
      <span class="text-xs font-medium">
        {{ item.question }}
        <span
          v-if="item.multiple && !submitted"
          class="ml-1 font-normal text-muted-foreground">
          Select all that apply
        </span>
      </span>
      <div class="flex flex-wrap gap-1.5">
        <button
          v-for="option in item.options"
          :key="option"
          type="button"
          class="ai-chip"
          :class="{
            'ai-chip-on': answerIncludes(answerOf(index), option),
            'inline-flex items-center gap-1': item.multiple,
          }"
          :role="item.multiple ? 'checkbox' : undefined"
          :aria-checked="item.multiple ? answerIncludes(answerOf(index), option) : undefined"
          :disabled="submitted || disabled"
          @click="pick(index, option)">
          <template v-if="item.multiple">
            <SquareCheck
              v-if="answerIncludes(answerOf(index), option)"
              class="size-3.5" />
            <Square
              v-else
              class="size-3.5" />
          </template>
          {{ option }}
        </button>
        <button
          v-if="!submitted"
          type="button"
          class="ai-chip border-dashed text-muted-foreground"
          :class="{ 'ai-chip-on': otherOpen[index] }"
          :disabled="disabled"
          @click="openOther(index)">
          Other…
        </button>
      </div>
      <Input
        v-if="otherOpen[index] && !submitted"
        v-model="otherText[index]"
        placeholder="Type your answer"
        class="h-8 text-xs" />
    </div>

    <div class="flex items-center gap-2 border-t border-border pt-2.5">
      <span class="mr-auto text-2xs text-muted-foreground">
        {{ submitted ? "Answers sent" : `${answeredCount} of ${form.questions.length} answered` }}
      </span>
      <Button
        v-if="!submitted"
        size="sm"
        :disabled="!complete || disabled"
        @click="submit">
        Send answers
      </Button>
    </div>
  </div>
</template>
